import { useQuery } from '@apollo/client'
import { PublisherInterface } from '@chili-publish/publisher-interface'
import config from 'config'
import { useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import styled from 'styled-components'

import useAuth from 'data/gradoo/hooks/useAuth'
import { CHECK_FOR_CUSTOM_COVER } from 'data/layoutcreator/queries/cover'
import { GET_PROJECT_COVER_DOCUMENTS } from 'data/layoutcreator/queries/documents'
import { GET_PROJECT_META } from 'data/layoutcreator/queries/projects'
import User from 'screens/YearbookEditor/components/User'
import EditorIframe from 'shared/components/EditorIframe'
import PageHeader from 'shared/components/PageHeader'
import Tools from 'shared/components/Tools'
import {
  EDITOR_PANEL_SIDE_MARGIN,
  EDITOR_PANEL_WIDTH
} from 'shared/consts'
import { useChiliApisInit } from 'shared/hooks/useChiliApisInit'
import useDisablePinchZoom from 'shared/hooks/useDisablePinchZoom'
import { useEditorSave } from 'shared/hooks/useEditorSave'
import { useEditorZoom } from 'shared/hooks/useEditorZoom'
import { CoverSides } from 'shared/types/global'
import Button from 'shared/ui/Button_2'
import IcomoonIcon from 'shared/ui/IcomoonIcon'
import Selector from 'shared/ui/Selector'

import CoverStartEditingPanel from './components/CoverStartEditingPanel'
import CustomCoverEditingPanel from './components/CustomCoverEditingPanel'
import CustomCoverPreview from './components/CustomCoverPreview'

const Container = styled.div`
  display: flex;
  /* flex-direction: column; */
  height: 100%;
  overflow: scroll;
`

const BottomActions = styled.div`
  position: absolute;
  bottom: 24px;
  margin-bottom: 4px;
  right: 50%;
  transform: translateX(50%);
  display: flex;
  gap: 8px;
  min-width: 260px;
`

const ZoomWrapper = styled.div`
  background: rgba(255, 255, 255, 0.8);
  box-shadow: 0px 3px 3px 0px rgba(0, 0, 0, 0.05);
  padding: 4px 8px;
  border-radius: 100px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid ${({ theme }) => theme.color.base.c2};
`

const ZoomText = styled.span`
  font-size: 12px;
  font-weight: 600;
  color: ${({ theme }) => theme.color.base.c6};
  min-width: 30px;
  text-align: center;
`

const MakeFitZoomButton = styled.button`
  color: ${({ theme }) => theme.color.brand_02};
  border: none;
  font-size: 12px;
  font-weight: 600;
  text-decoration: underline;
  text-underline-offset: 2px;
  background: transparent;
  cursor: pointer;
`

const ZoomIconButton = styled.button`
  display: flex;
  align-items: center;
  border: none;
  justify-content: center;
  background: transparent;
  cursor: pointer;
  padding: 0px 8px;

  &:hover {
    color: ${({ theme }) => theme.color.brand_02};
  }
`

const MoveIconButton = styled.button<{ isActive: boolean }>`
  background: rgba(255, 255, 255, 0.8);
  box-shadow: 0px 3px 3px 0px rgba(0, 0, 0, 0.05);
  cursor: pointer;
  border: 1px solid
    ${({ theme, isActive }) =>
      isActive ? theme.color.brand_02 : theme.color.base.c2};
  border-radius: 100px;
  padding: 4px 16px;
  display: flex;
  align-items: center;
  color: ${({ theme, isActive }) =>
    isActive ? theme.color.brand_02 : theme.color.base.c10};
  &:hover {
    color: ${({ theme }) => theme.color.brand_02};
  }
`

const CoverSideSwitchContainer = styled.div`
  position: absolute;
  top: 100px;
  padding-left: ${EDITOR_PANEL_SIDE_MARGIN + EDITOR_PANEL_WIDTH}px;
  left: 50%;
  transform: translateX(-50%);
`

const getCurrentDocumentId = (
  coverSide: CoverSides,
  documents?: any[]
): null => {
  if (!documents) {
    return null
  }

  return documents.find(doc => doc.name.includes(coverSide))?.id
}

const WORKSPACE_ID = config.chiliWorkspaceId
const SCREEN_HEIGHT = window.innerHeight
const SCREEN_WIDTH = window.innerWidth
// TODO: check if this can be sovled by changing zoom logic
const INITIAL_ZOOM = Math.round(SCREEN_HEIGHT / 12 / 10) * 10 // this is the ratio I discovered to workaround zoom inconsistency for cover

const YearbookCoverEditor: React.FC = () => {
  useDisablePinchZoom()

  const { pathname } = useLocation()
  const coverSide = pathname.endsWith('/outside')
    ? CoverSides.outside
    : CoverSides.inside

  const navigate = useNavigate()

  const { projectId } = useParams()
  const { authUserGroup, authGroupId } = useAuth()

  const [publisherConnector, setPublisherConnector] =
    useState<PublisherInterface | null>(null)
  const [isDocumentLoaded, setIsDocumentLoaded] = useState(false)
  const [isWorkspaceRendered, setIsWorkspaceRendered] =
    useState(false)
  const [isSwitchingCover, setIsSwitchingCover] = useState(false)

  const { data: projectMetaData, loading: loadingProjectMeta } =
    useQuery(GET_PROJECT_META, {
      variables: {
        projectId: projectId || ''
      },
      skip: !authGroupId || !projectId
    })

  const { data: coverDocumentsData, loading: loadingCoverDocuments } =
    useQuery(GET_PROJECT_COVER_DOCUMENTS, {
      variables: {
        groupId: authGroupId || '',
        projectId: projectId || ''
      },
      skip: !authGroupId || !projectId
    })

  const { data: checkForCustomCoverData } = useQuery(
    CHECK_FOR_CUSTOM_COVER,
    {
      variables: {
        projectId: projectId!
      }
    }
  )
  const withCustomCover =
    !!checkForCustomCoverData?.checkForCustomCover?.[coverSide]

  const isSoftCover = useMemo(() => {
    return projectMetaData?.getProjectMeta.coverType === 'soft'
  }, [projectMetaData])

  const documentId = useMemo(() => {
    const documents = coverDocumentsData?.findProjectCoverDocuments

    const result = getCurrentDocumentId(coverSide, documents)

    if (!result && documents) {
      navigate(-1)
      throw new Error(`Cover page isn't found`)
    }

    return result
  }, [coverSide, coverDocumentsData])

  useChiliApisInit({
    publisherConnector: publisherConnector!,
    isDocumentLoaded
  })

  useEffect(() => {
    setIsSwitchingCover(true)
    setPublisherConnector(null)
    setIsDocumentLoaded(false)
    setIsWorkspaceRendered(false)
  }, [coverSide])

  useEffect(() => {
    if (isDocumentLoaded || withCustomCover) {
      setIsSwitchingCover(false)
    }
  }, [isDocumentLoaded, withCustomCover])

  const {
    onZoomFit,
    onZoomIn,
    onZoomOut,
    onMoveToggle,
    initialZoom,
    currentZoomLabel,
    currentZoomValue,
    cursorType
  } = useEditorZoom({
    publisherConnector: publisherConnector!,
    isWorkspaceRendered,
    initialZoomValue: INITIAL_ZOOM
  })

  const { handleSave, isLoading: isDocumentSaveLoading } =
    useEditorSave({
      publisherConnector,
      backPath: `/${projectId}/cover`,
      groupId: authGroupId!,
      projectId: projectId!,
      pageId: documentId!
    })

  const setCanvasMargins = async () => {
    const HEADER_HEIGHT = 54

    const calculateLeftPageMargin = () => {
      const leftPanelIndent =
        EDITOR_PANEL_SIDE_MARGIN + EDITOR_PANEL_WIDTH

      return (SCREEN_WIDTH - leftPanelIndent) / 3
    }

    const calculatePageFitMarginX = () => {
      return SCREEN_WIDTH / 4
    }

    const calculatePageCanvasMarginY = () => {
      return HEADER_HEIGHT + 128
    }

    const pageFitMarginX = calculatePageFitMarginX()
    const pageFitMarginY = 140
    const pageCanvasMarginX = calculateLeftPageMargin()
    const pageCanvasMarginY = calculatePageCanvasMarginY()

    await publisherConnector?.setProperty(
      'document.viewPreferences',
      'pageCanvasMarginX',
      pageCanvasMarginX
    )
    await publisherConnector?.setProperty(
      'document.viewPreferences',
      'pageCanvasMarginY',
      pageCanvasMarginY
    )
    await publisherConnector?.setProperty(
      'document.viewPreferences',
      'pageFitMarginY',
      pageFitMarginY
    )
    await publisherConnector?.setProperty(
      'document.viewPreferences',
      'pageFitMarginX',
      pageFitMarginX
    )
  }

  useEffect(() => {
    if (!publisherConnector && !isDocumentLoaded) {
      return
    }

    setCanvasMargins()
  }, [publisherConnector, isDocumentLoaded])

  return (
    <Container>
      <PageHeader
        titleId={'Editor.title'}
        withBackButton
        onBackButtonClick={
          withCustomCover
            ? undefined
            : () => {
                if (isDocumentLoaded) {
                  handleSave()
                }
              }
        }
        renderBackButton={handleGoBack => (
          <Button
            loading={isDocumentSaveLoading}
            textId="Editor.leaveButton"
            intent="primary-brand-02"
            onPress={handleGoBack}
            icon="left"
            iconName="save"
            minWidth={216}
          />
        )}
        tools={
          <Tools
            publisherConnector={publisherConnector!}
            isDocumentLoaded={isDocumentLoaded}
            showRightPanel={false}
            showLeftPanel={withCustomCover ? true : !isSwitchingCover}
            renderDefaultLeftPanel={() =>
              withCustomCover ? (
                <CustomCoverEditingPanel coverSide={coverSide} />
              ) : (
                <CoverStartEditingPanel
                  withCustomCover={withCustomCover}
                  coverSide={coverSide}
                />
              )
            }
          />
        }
        user={
          authUserGroup ? (
            <User authUserGroup={authUserGroup} />
          ) : undefined
        }
      />

      {isSoftCover && (
        <CoverSideSwitchContainer>
          <Selector options={['outside', 'inside']} />
        </CoverSideSwitchContainer>
      )}

      {withCustomCover && <CustomCoverPreview side={coverSide} />}

      {!withCustomCover && documentId && (
        <EditorIframe
          pageId={documentId!}
          workspaceId={WORKSPACE_ID}
          onBuild={publisherConnector => {
            setPublisherConnector(publisherConnector)
          }}
          onDocumentLoad={() => {
            setIsDocumentLoaded(true)
          }}
          onWorkspaceRender={() => setIsWorkspaceRendered(true)}
        />
      )}

      {withCustomCover ? (
        <></>
      ) : (
        <BottomActions>
          <MoveIconButton
            isActive={cursorType === 'hand'}
            onClick={onMoveToggle}
          >
            <IcomoonIcon size={24} icon="move" />
          </MoveIconButton>
          {initialZoom.current && (
            <ZoomWrapper>
              <ZoomIconButton onClick={onZoomOut}>
                <IcomoonIcon size={24} icon="zoom-out" />
              </ZoomIconButton>

              <ZoomText>{currentZoomLabel}%</ZoomText>

              <ZoomIconButton onClick={onZoomIn}>
                <IcomoonIcon size={24} icon="zoom-in" />
              </ZoomIconButton>
              {initialZoom.current !== currentZoomValue && (
                <MakeFitZoomButton onClick={onZoomFit}>
                  Make fit
                </MakeFitZoomButton>
              )}
            </ZoomWrapper>
          )}
        </BottomActions>
      )}
    </Container>
  )
}

export default YearbookCoverEditor
